/**
 * Flow Optimizer formula — AST + Parser
 * For Media Devoted | Created By Ciro Autiero
 *
 * Recursive-descent parser producing a typed AST. Operator precedence:
 *   unary `-`  >  `* / %`  >  `+ -`
 * Functions allowed: min, max, abs, log, sqrt, ln (alias for log), and
 * lowest(metric) — "least is best" (lowest/lowest.ts).
 */

import { tokenize, type Token } from "./tokenizer";
import { assertLowestArgs } from "./lowest/lowest";

export type AllowedFn = "min" | "max" | "abs" | "log" | "ln" | "sqrt" | "lowest";

export type AstNode =
  | { kind: "num"; value: number }
  | { kind: "ident"; name: string }
  | { kind: "unary"; op: "-"; operand: AstNode }
  | {
      kind: "binary";
      op: "+" | "-" | "*" | "/" | "%";
      left: AstNode;
      right: AstNode;
    }
  | { kind: "call"; name: AllowedFn; args: AstNode[] };

const FNS: AllowedFn[] = ["min", "max", "abs", "log", "ln", "sqrt", "lowest"];
export const ALLOWED_FNS: ReadonlySet<AllowedFn> = new Set(FNS);

export function parseFormula(source: string): AstNode {
  const tokens = tokenize(source);
  const cursor = { index: 0 };
  const ast = parseAddSub(tokens, cursor);
  expect(tokens, cursor, "eof");
  return ast;
}

function parseAddSub(tokens: Token[], cursor: { index: number }): AstNode {
  let left = parseMulDiv(tokens, cursor);
  while (peekOp(tokens, cursor, "+", "-")) {
    const op = tokens[cursor.index++].value as "+" | "-";
    const right = parseMulDiv(tokens, cursor);
    left = { kind: "binary", op, left, right };
  }
  return left;
}

function parseMulDiv(tokens: Token[], cursor: { index: number }): AstNode {
  let left = parseUnary(tokens, cursor);
  while (peekOp(tokens, cursor, "*", "/", "%")) {
    const op = tokens[cursor.index++].value as "*" | "/" | "%";
    const right = parseUnary(tokens, cursor);
    left = { kind: "binary", op, left, right };
  }
  return left;
}

function parseUnary(tokens: Token[], cursor: { index: number }): AstNode {
  if (peekOp(tokens, cursor, "-")) {
    cursor.index++;
    const operand = parseUnary(tokens, cursor);
    return { kind: "unary", op: "-", operand };
  }
  if (peekOp(tokens, cursor, "+")) {
    cursor.index++;
    return parseUnary(tokens, cursor);
  }
  return parsePrimary(tokens, cursor);
}

function parsePrimary(tokens: Token[], cursor: { index: number }): AstNode {
  const token = tokens[cursor.index];
  if (token.type === "number") {
    cursor.index++;
    return { kind: "num", value: Number(token.value) };
  }
  if (token.type === "ident") return parseIdentOrCall(tokens, cursor);
  if (token.type === "lparen") return parseGrouped(tokens, cursor);
  throw new Error(`Formula parse error at ${token.position}: unexpected '${token.value}'`);
}

function parseGrouped(tokens: Token[], cursor: { index: number }): AstNode {
  cursor.index++;
  const expr = parseAddSub(tokens, cursor);
  expect(tokens, cursor, "rparen");
  return expr;
}

function parseIdentOrCall(
  tokens: Token[],
  cursor: { index: number },
): AstNode {
  const token = tokens[cursor.index++];
  if (tokens[cursor.index]?.type !== "lparen") {
    return { kind: "ident", name: token.value };
  }
  if (!ALLOWED_FNS.has(token.value as AllowedFn)) {
    throw new Error(
      `Formula parse error at ${token.position}: function '${token.value}' is not allowed`,
    );
  }
  cursor.index++;
  const args = parseArgList(tokens, cursor);
  if (token.value === "lowest") assertLowestArgs(args, token.position);
  return { kind: "call", name: token.value as AllowedFn, args };
}

function parseArgList(tokens: Token[], cursor: { index: number }): AstNode[] {
  const args: AstNode[] = [];
  if (tokens[cursor.index]?.type === "rparen") {
    cursor.index++;
    return args;
  }
  args.push(parseAddSub(tokens, cursor));
  while (tokens[cursor.index]?.type === "comma") {
    cursor.index++;
    args.push(parseAddSub(tokens, cursor));
  }
  expect(tokens, cursor, "rparen");
  return args;
}

function peekOp(
  tokens: Token[],
  cursor: { index: number },
  ...ops: string[]
): boolean {
  const token = tokens[cursor.index];
  return token.type === "op" && ops.includes(token.value);
}

function expect(
  tokens: Token[],
  cursor: { index: number },
  type: Token["type"],
): void {
  const token = tokens[cursor.index];
  if (token.type !== type) {
    throw new Error(
      `Formula parse error at ${token.position}: expected ${type}, got ${token.type}`,
    );
  }
  cursor.index++;
}
